import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Toolbar, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SettingsOptions from '../../../components/Settings/SettingsOptions';
import { FlexBox } from '../../../components/tableItems';
import SearchInput from '../../../components/Search/SearchInput';
import AliasTable from './AliasTable/AliasTable';
import DrawerLinks from '../../DefaultView/DrawerLinks';
import AliasFilter from './AliasFilter/AliasFilter';
import useAliasStore from '../../../stores/alias/useAliasStore';
import { AliasResultsI, defaultAliasRequest } from '../../../models/Alias';
import {
  editActiveAlias,
  getAllAlias,
  postNewAlias,
  sendTestAliasEndpoint,
} from '../../../helpers/alias';
import useAllUsers from '../../../customHooks/useAllUsers';
import Context from '../../../context/UserContext';
import { UserContextType } from '../../../models/User';
import useSites from '../../../customHooks/useSites';
import useProjects from '../../../customHooks/useProjects';
import { MainButton } from '../../../components/buttons';
import MainDrawer from '../../../components/Drawers/MainDrawer';
import AliasForm from './AliasForm/AliasForm';
import { AliasFormInterface, AliasFormObjectDefault } from './AliasConstants';
import FeedbackMessage from '../../../components/FeedbackMessage';
import { SeverityType } from '../../../models/General';

const Alias = () => {
  const { UserContext } = useContext(Context) as UserContextType;
  const [drawerFilter, setDrawerFilter] = useState(false);
  const updateDrawerFilter = () => setDrawerFilter(!drawerFilter);
  const { owners: users, loading: loadingUsers } = useAllUsers();
  const { sites, loading: loadingSites } = useSites({
    minimal: true,
  });

  const [aliasTableResults, setAliasTableResults] = useState<AliasResultsI[]>(
    [],
  );
  const [count, setCount] = useState(0);
  const [drawer, setDrawer] = useState(false);
  const closeDrawer = () => setDrawer(false);
  const openDrawer = () => setDrawer(true);
  const [aliasFormObject, setAliasFormObject] = useState<AliasFormInterface>(
    AliasFormObjectDefault,
  );
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const [formType, setFormType] = useState<'add' | 'view'>('add');
  const [idEdit, setIdEdit] = useState(0);

  const isFirstRender = useRef(true);
  const {
    loadingAlias,
    updateLoadingAlias,
    aliasRequestObject,
    updateAliasRequest,
    updateFiltersChange,
    updateFiltersDefault,
    filterValuesChange,
  } = useAliasStore();

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<{
    message: string;
    severity: SeverityType;
  }>({ message: '', severity: 'success' });

  const fetchAliasResults = async () => {
    setAliasTableResults([]);
    if (!loadingAlias) updateLoadingAlias(true);
    try {
      const resp = await getAllAlias(
        aliasRequestObject.page,
        aliasRequestObject.page_size,
        aliasRequestObject.filters,
        aliasRequestObject.ordering,
      );
      setCount(resp.count);
      setAliasTableResults(resp.results);
    } catch (err) {
      setAliasTableResults([]);
      setCount(0);
    } finally {
      updateLoadingAlias(false);
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchAliasResults();
  }, [aliasRequestObject]);

  useEffect(() => {
    return () => {
      setAliasTableResults([]);
      updateLoadingAlias(true);
      updateAliasRequest(defaultAliasRequest);
      setCount(0);
    };
  }, []);

  const resetForm = () => setAliasFormObject(AliasFormObjectDefault);

  const submitNewAlias = async () => {
    setLoadingSubmit(true);
    try {
      const copyObj = { ...aliasFormObject };
      const siteFind = sites.find(item => item.id === copyObj.site_id);
      copyObj.smtp_alias += `@${siteFind?.name.toLowerCase()}`;
      await postNewAlias(copyObj);

      setFeedbackMessage({
        message: 'Alias created successfully',
        severity: 'success',
      });
      setFeedbackOpen(true);
      setAliasFormObject(AliasFormObjectDefault);
      closeDrawer();
      fetchAliasResults();
    } catch (err) {
      setFeedbackMessage({
        message: 'Error creating alias',
        severity: 'error',
      });
      setFeedbackOpen(true);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const updateAlias = async (obj: AliasFormInterface) => {
    setLoadingSubmit(true);
    try {
      await editActiveAlias(obj, idEdit);

      setFeedbackMessage({
        message: 'Alias updated successfully',
        severity: 'success',
      });
      setFeedbackOpen(true);
      setAliasFormObject(AliasFormObjectDefault);
      setIdEdit(0);
      closeDrawer();
      fetchAliasResults();
    } catch (err) {
      setFeedbackMessage({
        message: 'Error updating alias',
        severity: 'error',
      });
      setFeedbackOpen(true);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const sendTestAliasPost = async () => {
    setLoadingSubmit(true);
    try {
      await sendTestAliasEndpoint(idEdit);

      setFeedbackMessage({
        message: 'Test sent successfully',
        severity: 'success',
      });
      setFeedbackOpen(true);
      setAliasFormObject(AliasFormObjectDefault);
      setIdEdit(0);
      closeDrawer();
      fetchAliasResults();
    } catch (err) {
      setFeedbackMessage({
        message: 'Error sending test',
        severity: 'error',
      });
      setFeedbackOpen(true);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const viewAlias = (obj: AliasFormInterface, id: number) => {
    setFormType('view');
    setIdEdit(id);
    setAliasFormObject(obj);
    openDrawer();
  };

  return (
    <div className="grid_container_settings">
      <DrawerLinks
        openCloseDrawer={updateDrawerFilter}
        openDrawer={drawerFilter}
      >
        <AliasFilter
          updateDrawerFilter={updateDrawerFilter}
          users={users}
          sites={sites}
        />
      </DrawerLinks>
      <div className="left_columns">
        <SettingsOptions option={2} />
      </div>
      <div className="right_columns">
        <Box>
          <Toolbar />
          <Box sx={{ pt: 4 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              px={3}
            >
              <FlexBox>
                <IconButton
                  aria-label="filter"
                  disabled={false}
                  id="long-button"
                  sx={{
                    border: 1,
                    borderRadius: '10%',
                    bgcolor: '',
                    '&.Mui-disabled': {
                      bgcolor: '',
                    },
                  }}
                  aria-haspopup="true"
                  onClick={() => updateDrawerFilter()}
                >
                  <FilterListIcon
                    sx={{
                      color: 'black',
                      '&:hover': { color: 'green' },
                    }}
                  />
                </IconButton>
                <Typography ml={1} fontSize={17} fontWeight="bold">
                  Filters
                </Typography>
              </FlexBox>
              <Typography fontSize={24} fontWeight={900}>
                Alias
              </Typography>
              <MainButton
                disabled={loadingAlias}
                variant="contained"
                sx={{
                  color: 'black',
                  backgroundColor: '#BAF372',
                  '&:hover': {
                    backgroundColor: '#BAF372',
                  },
                }}
                type="submit"
                onClick={() => {
                  setFormType('add');
                  openDrawer();
                }}
              >
                Add Alias
              </MainButton>
            </Box>

            <Box mt={4}>
              <Box pl={3} pr={4}>
                <SearchInput
                  placeholder="Search by alias or username"
                  id="search"
                  filter={false}
                  onKeyDown={() => updateFiltersDefault(filterValuesChange)}
                  onClear={() => {
                    const copyObj = { ...filterValuesChange };
                    updateFiltersChange({
                      search: { id: 'search', value: '' },
                    });
                    copyObj.search = { id: 'search', value: '' };
                    updateFiltersDefault(copyObj);
                  }}
                  value={filterValuesChange.search.value}
                  onChangeNormal={query =>
                    updateFiltersChange({
                      search: { id: 'search', value: query },
                    })
                  }
                  disabled={loadingAlias}
                />
              </Box>

              <Box my={4}>
                <AliasTable
                  aliasTableResults={aliasTableResults}
                  count={count}
                  viewAlias={viewAlias}
                  fetchAlias={fetchAliasResults}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <MainDrawer openDrawer={drawer} setOpenDrawer={setDrawer}>
          <AliasForm
            type={formType}
            closeDrawer={closeDrawer}
            sites={sites}
            aliasFormObject={aliasFormObject}
            resetForm={resetForm}
            setAliasFormObject={setAliasFormObject}
            users={users.map(item => {
              return { id: item.id, name: item.username };
            })}
            loadingSubmit={loadingSubmit}
            submitNewAlias={submitNewAlias}
            updateAlias={updateAlias}
            sendTestAlias={sendTestAliasPost}
          />
        </MainDrawer>
      </div>
      <FeedbackMessage
        open={feedbackOpen}
        setOpen={setFeedbackOpen}
        vertical="top"
        horizontal="right"
        severity={feedbackMessage.severity}
        message={feedbackMessage.message}
      />
    </div>
  );
};

export default Alias;
